// The two questions a planner on the `ExistingNameOrigin` contract has to
// settle BEFORE it hands a column list to a synthesizer — shared because the
// key / index planners all ask them and a second copy is how two answers
// drift apart (the lesson `columnEdit.alterTable.ts`'s exported
// `sourceSpellingOrigin` already records).
//
//   1. Did the CALLER'S OWN spelling assert an origin? `decodeTypedColumnList`
//      strips the delimiters a user typed in a free-text column list, exactly
//      once, and reports what they asserted.
//   2. Is the relation this edit names the one the SOURCE STATEMENT targets?
//      `namesSameRelation` answers it, and only a `true` there lets a planner
//      read the source's clauses as evidence about those columns.
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice H2;
//      Ralph/NavigationalFeatures/PRD.md § "Identifier-chain quoting — per
//      part, never per chain".

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import { foldIdentifier } from '../../scope/identifiers';

import {
  decodeDelimitedIdentifier,
  decodeTypedColumnRef,
  isBareIdentifierShape,
  type ExistingNameOrigin,
} from './dialect.quoting';

/** WHERE the element at each POSITION of ONE column list came from — the shape
 *  every synthesizer asks with.
 *
 *  **By position, never by name.** A column's identity is its bytes PLUS its
 *  delimiters, so `Id` and `"Id"` are two different columns on a folding
 *  engine — which makes the stripped name the one fact that cannot tell an
 *  occurrence apart from its sibling. A list keyed on it hands the second
 *  element's answer to the first: a typed `Id, "Id"` went out as
 *  `("Id", "Id")`, one column named twice where two were meant. The index into
 *  the list the synthesizer is emitting IS the occurrence, so that is what the
 *  question is asked with. */
export type ColumnListOrigins = (index: number) => ExistingNameOrigin;

/** A caller's column list with its delimiters off, plus what those delimiters
 *  asserted PER POSITION. `typedOriginAt` is `undefined` for an element that
 *  asserted nothing — the planner asks the SOURCE STATEMENT next, and falls
 *  back to `'bare-reference'`. */
export type DecodedColumnList = {
  readonly columns: readonly string[];
  readonly typedOriginAt: (index: number) => ExistingNameOrigin | undefined;
};

/** Strip the delimiters each element was typed with, recording the ones that
 *  had any — those spellings answer the origin question themselves and must
 *  not be re-asked of the source. See `dialect.quoting.ts`'s
 *  {@link decodeTypedColumnRef} for why the delimiters come off here and
 *  exactly once.
 *
 *  The answers stay ALONGSIDE their elements rather than in a by-name map:
 *  two entries of one list may decode to the same bytes (`Id, "Id"` is `id`
 *  and `Id` on a folding engine) and a map would let the later one overwrite
 *  the earlier one's intent, then serve that same answer back to both. */
export function decodeTypedColumnList(
  engine: SqlEngine,
  columns: readonly string[],
): DecodedColumnList {
  const decoded = columns.map((typed) => decodeTypedColumnRef(typed, engine));
  return {
    columns: decoded.map(({ name }) => name),
    typedOriginAt: (index) => decoded[index]?.origin,
  };
}

/** Whether two `QualifiedName`s denote the SAME RELATION on `engine`.
 *
 *  A statement speaks for its OWN relation's columns and nothing else, so this
 *  is the gate on borrowing a source statement's spellings — an index built on
 *  another table, a foreign key pointing at one. Reading
 *  `ALTER TABLE orders ADD COLUMN "Id" …` as evidence about `users.Id` would
 *  declare a delimited spelling for a column that may well be bare.
 *
 *  **Component by component, never by the chain-wide flag.** `QualifiedName`
 *  records ONE `quoted` for the whole chain (`parts.some(p => p.quoted)`), so
 *  PostgreSQL's `"public".T` and `public."T"` both carry `quoted: true` — two
 *  DIFFERENT tables (`public.t` and `public.T`) that a chain-wide comparison
 *  equates, then hands each other's column spellings. That is the same defect
 *  this branch's round-12 P1 recorded in `Ralph/NavigationalFeatures/PRD.md`
 *  ("Identifier-chain quoting — per part, never per chain"), whose ruling is
 *  the one followed here: identities built for one part compare on that part's
 *  own facts only.
 *
 *  **A component whose identity cannot be established refuses the match.**
 *  Refusing costs a caller nothing it was entitled to — every one of them
 *  falls back to `'bare-reference'`, the contract a caller holding only a name
 *  is under. Claiming an unprovable match costs a column: it moves a delimited
 *  spelling onto another relation's catalog. */
export function namesSameRelation(
  a: QualifiedName,
  b: QualifiedName,
  engine: SqlEngine,
): boolean {
  const nameA = componentIdentity(a.name, leafWasQuoted(a), a.foldedName, engine);
  const nameB = componentIdentity(b.name, leafWasQuoted(b), b.foldedName, engine);
  if (nameA === null || nameA !== nameB) return false;
  return sameQualifier(a, b, 'schema', engine) && sameQualifier(a, b, 'database', engine);
}

/** One qualifier slot. BOTH sides must name it or NEITHER may: a bare `users`
 *  and a `public.users` are the same table only when `public` is what the
 *  session's search path resolves the bare name to, and `applyDdlEdit` reads
 *  no catalog and holds no session — it cannot know. Treating the missing half
 *  as a wildcard is how a statement on one schema's table came to speak for
 *  another schema's. */
function sameQualifier(
  a: QualifiedName,
  b: QualifiedName,
  slot: 'schema' | 'database',
  engine: SqlEngine,
): boolean {
  const bytesA = slot === 'schema' ? a.schema : a.database;
  const bytesB = slot === 'schema' ? b.schema : b.database;
  if (bytesA === undefined && bytesB === undefined) return true;
  const idA = componentIdentity(
    bytesA, qualifierWasQuoted(a), slot === 'schema' ? a.foldedSchema : a.foldedDatabase, engine,
  );
  const idB = componentIdentity(
    bytesB, qualifierWasQuoted(b), slot === 'schema' ? b.foldedSchema : b.foldedDatabase, engine,
  );
  return idA !== null && idA === idB;
}

/** WHICH OBJECT one component of a chain names, or `null` when this record
 *  cannot say.
 *
 *  Established from the component's OWN quoting when that is recorded —
 *  delimiters suppress the fold, so a delimited part's bytes are the identity
 *  and a bare part's is what the engine folds it to. When it is not recorded,
 *  the PRE-FOLDED spelling (`foldedName` / `foldedSchema` / `foldedDatabase`)
 *  is the only per-part fact left, and it is trusted only when it IS one of
 *  the two foldings these bytes could have. A record whose folded field is
 *  neither is self-inconsistent — hand-built by a caller that got it wrong —
 *  and names nothing this comparison may rely on. */
function componentIdentity(
  bytes: string | undefined,
  wasQuoted: boolean | undefined,
  folded: string | undefined,
  engine: SqlEngine,
): string | null {
  if (bytes === undefined) return null;
  if (wasQuoted !== undefined) return wasQuoted ? bytes : bareFold(bytes, engine);
  if (folded === undefined) return null;
  return folded === bytes || folded === bareFold(bytes, engine) ? folded : null;
}

/** What a BARE spelling of `bytes` matches on `engine` — the app-wide MATCHING
 *  fold, which is the one `foldedName` is built with, so the two halves of
 *  {@link componentIdentity} answer in one lens.
 *
 *  Deliberately NOT `dialect.quoting.ts`'s `foldAsStored`: that one says how a
 *  server STORES a bare name, and mssql / mysql / sqlite store the typed case
 *  while matching it case-insensitively — a different question, as that file's
 *  own header records. */
function bareFold(bytes: string, engine: SqlEngine): string {
  return foldIdentifier(bytes, false, { engine });
}

/** Whether the LEAF part carried delimiters — `undefined` when this record
 *  cannot say. `qn.quoting` IS the leaf's own (both readers populate it from
 *  the last part's quote char). Failing that, a chain with no quoted part at
 *  all proves the leaf was bare, and a one-part chain's chain-wide flag IS the
 *  leaf's. A quoted multi-part chain proves nothing about the leaf — the
 *  quotes may have been on a qualifier. */
function leafWasQuoted(qn: QualifiedName): boolean | undefined {
  if (qn.quoting !== undefined) return qn.quoting !== 'unquoted';
  if (!qn.quoted) return false;
  if (qn.schema === undefined && qn.database === undefined) return true;
  return undefined;
}

/** Whether a QUALIFIER carried delimiters. `QualifiedName` keeps no per-
 *  qualifier quoting at all (`quoting` is the leaf's), so the only thing
 *  provable here is the negative — a chain with no quoted part had none.
 *  Everything else falls through to the qualifier's own pre-folded spelling. */
function qualifierWasQuoted(qn: QualifiedName): boolean | undefined {
  return qn.quoted ? undefined : false;
}


/** Words that are a VALID INDEX KEY on their own — an entry the server reads
 *  as an expression rather than as a reference to a same-named column.
 *
 *  MEASURED on the docker servers, because reasoning from "it is a keyword"
 *  gets this wrong in both directions. `CREATE INDEX i ON t (NULL)` against a
 *  table holding a column named `NULL`:
 *
 *    sqlite    → an EXPRESSION index (`PRAGMA index_info` column -2)
 *    oracle    → `Index created` — an expression index
 *    postgres  → `syntax error at or near "NULL"`
 *    mysql     → `ERROR 1064 … near 'NULL)'`
 *    mssql     → `Msg 16216`
 *
 *  So only two engines can mean something else by it. Everywhere else the
 *  entry is not valid SQL at all, and naming the column is the only reading
 *  that can have been intended.
 *
 *  `NULL` is the whole set on SQLite and is NOT the whole set on Oracle — see
 *  the table below, which is measured per word rather than inferred from any
 *  keyword list. Treating "the keyword-shaped words" as one class is what made
 *  the previous two attempts at this rule wrong in both directions.
 *
 *  This deliberately replaced a broader "any reserved word" gate. That gate
 *  closed the `NULL` case but made the index form DIVERGE from the key forms
 *  for every other reserved word: a typed `order` against a source declaring
 *  `"order"` emitted `("order")` from the primary-key form and a bare
 *  `(order)` — which every server rejects — from this one. A word that cannot
 *  be a valid index key is not an expression a caller might have meant; it is
 *  a column the caller should have delimited, and naming it is what the three
 *  sibling forms already do.
 *
 *  Other keyword-shaped candidates were measured and are NOT here: `TRUE` /
 *  `FALSE` resolve to a same-named column on sqlite, oracle and mssql;
 *  `CURRENT_DATE`, `SYSDATE`, `SYSTIMESTAMP` and friends are refused as index
 *  expressions (Oracle ORA-01743, PostgreSQL's IMMUTABLE check), so naming
 *  the column turns a server error into the index the user asked for. */
const INDEX_KEY_LITERAL: Record<SqlEngine, ReadonlySet<string>> = {
  sqlite: new Set(['null']),
  // Oracle has more than `NULL`, and the list is MEASURED, not read off a
  // keyword table: each word below built an index with a `user_ind_expressions`
  // row against `CREATE TABLE t ("<word>" NUMBER, x NUMBER)`, i.e. the server
  // read the bare token as the literal even though a column of that exact name
  // was in scope. The four `BINARY_*` are the floating-point literals; the four
  // `ORA_INVOKING_*` are pure functions Oracle will index — ALL FOUR that
  // Oracle documents, which is the point: the first pass enumerated candidates
  // by hand and stopped at three, while the docs globbed `ORA_INVOKING_*` and
  // so promised the fourth. A set measured from a hand-written candidate list
  // is only as complete as that list; a DOCUMENTED family is the one place
  // completeness can actually be checked, so check it there.
  //
  // Measured in the same run and deliberately ABSENT: `TRUE` / `FALSE` /
  // `ORA_DST_*` / `SYS_CONTEXT` / `SYS_GUID` resolve to the column, and
  // `SYSDATE` / `USER` / `UID` / `DBTIMEZONE` (ORA-01743), `ORA_ROWSCN`
  // (ORA-43807) and `ROWID` (ORA-03050) are refused outright — for all of
  // those, naming the column is the only reading that can work.
  oracle: new Set([
    'null',
    'binary_double_nan', 'binary_float_nan',
    'binary_double_infinity', 'binary_float_infinity',
    'ora_invoking_user', 'ora_invoking_userid',
    'ora_invoking_xs_user', 'ora_invoking_xs_user_guid',
  ]),
  postgres: new Set(),
  mysql: new Set(),
  mssql: new Set(),
};

/** An INDEX entry is not a column list element — it is a verbatim SEGMENT of
 *  the index's own grammar. `a DESC`, `LOWER(email)`, MySQL's `col(10)`,
 *  `NULLS FIRST` and a PostgreSQL opclass all ride the same `string` arm, and
 *  a caller that decoded them as names would corrupt every one of them.
 *
 *  So this answers the narrower question the caller can actually act on: is
 *  this entry a column reference and NOTHING ELSE? Only then does the name
 *  behind it become knowable, which is what puts it on the
 *  {@link ExistingNameOrigin} contract the key lists have always been on.
 *  `null` for everything else, which keeps its bytes exactly as they are.
 *
 *  Two shapes qualify. A WHOLE-STRING delimited spelling (`"Id"`, `[Id]`,
 *  `` `Id` ``) names the catalog's own bytes — the same answer
 *  `decodeTypedColumnRef` gives a key list. A bare spelling qualifies when the
 *  WHOLE string is a legal bare identifier on this engine and is not one of
 *  the few words that engine reads as a KEY IN ITS OWN RIGHT
 *  ({@link INDEX_KEY_LITERAL}): that is what rejects `a DESC` and
 *  `LOWER(email)`, whose characters no bare identifier may hold, and `NULL` on
 *  the two engines where it builds an expression index. A partially delimited
 *  entry (`"Id" DESC`) fails both and stays verbatim, as it must — its
 *  trailing text is grammar, not name. */
export function decodeTypedIndexColumnRef(
  typed: string,
  engine: SqlEngine,
): { readonly name: string; readonly origin?: ExistingNameOrigin } | null {
  const { name, quoting } = decodeDelimitedIdentifier(typed, engine);
  if (quoting !== 'unquoted') return { name, origin: 'catalog-spelling' };
  if (!isBareIdentifierShape(typed, engine)) return null;
  return INDEX_KEY_LITERAL[engine].has(typed.toLowerCase()) ? null : { name };
}

/** An INCLUDE entry, which is a DIFFERENT question from a key entry: the
 *  INCLUDE grammar admits column names ONLY — no direction, no expression, no
 *  opclass — on both engines that have the clause. So every entry names a
 *  column and the key list's literal gate must not be applied: it would send
 *  a bare `order` out undelimited into a clause that has no other reading,
 *  which is invalid SQL. This is exactly the key synthesizers' own INCLUDE
 *  answer (`decodeTypedColumnRef`), reached by its own positional resolver so
 *  an overlapping KEY and INCLUDE can never share one. */
export function decodeTypedIncludeColumnRef(
  typed: string,
  engine: SqlEngine,
): { readonly name: string; readonly origin?: ExistingNameOrigin } {
  return decodeTypedColumnRef(typed, engine);
}
